"use client";

import Link from "next/link";
import { createPortal } from "react-dom";
import { useEffect, useId, useRef, useState } from "react";

/**
 * One top-level nav item with a panel of links under it.
 *
 * THE TRIGGER IS A LINK, AND THE PANEL OPENS ON HOVER AND ON FOCUS. The first version made it
 * a button that toggled on click, which read well and behaved badly: the pointer had already
 * opened the panel on the way to the click, so clicking closed it again. Anyone who hovered
 * and then clicked, which is most people, watched the menu shut in their face.
 *
 * So the trigger goes where it says it goes. Hovering or focusing it opens the panel, clicking
 * or pressing Enter navigates to the hub page, Escape closes the panel and returns focus, and
 * moving focus out of the group closes it. Nothing here is hover only: every destination in
 * the panel is reachable by keyboard, and on a touch device the tap lands on the hub page,
 * which lists the same links in full. It keeps aria-expanded and aria-haspopup because it is
 * still a disclosure, and it never traps anybody behind an interaction they cannot perform.
 *
 * THE PANEL IS A PORTAL, AND HAS TO BE. The first version positioned it inside the nav,
 * which is the obvious way to do it and rendered nothing at all: the header row carries
 * `overflow-x-clip`, and a box with one axis clipped and the other visible clips both, so the
 * panel was being cut off by a row 84 pixels tall. Removing that class would have traded a
 * working menu for horizontal overflow. So the panel goes to the body, positioned from the
 * trigger's own rectangle, under the mobile overlay's z-index and above everything else.
 *
 * A SMALL, DELIBERATE DELAY ON CLOSE. Moving the pointer from the trigger to the first link
 * crosses a gap, and closing the instant the pointer leaves the trigger makes the menu feel
 * broken. 120ms is long enough to cross and short enough not to feel sticky.
 */

export type NavMenuSection = {
  /** Optional heading above a group of links, for panels with more than one column. */
  heading?: string;
  links: readonly { label: string; href: string; note?: string }[];
};

export function NavMenu({
  label,
  href,
  sections,
  columns = 1,
  linkClass,
}: {
  label: string;
  /** The hub this item is for. Always reachable, whether or not the panel is used. */
  href: string;
  sections: readonly NavMenuSection[];
  columns?: 1 | 2 | 3;
  linkClass: string;
}) {
  const [open, setOpen] = useState(false);
  /* Where the panel sits, measured from the trigger rather than inherited from it. */
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const panelId = useId();
  const groupRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const closeSoon = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => cancelClose, []);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setAnchor({ top: rect.bottom + 12, left: rect.left + rect.width / 2 });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    const onPointerDown = (event: PointerEvent) => {
      const node = event.target as Node;
      if (groupRef.current?.contains(node) || panelRef.current?.contains(node)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  return (
    <div
      ref={groupRef}
      className="relative"
      onPointerEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onPointerLeave={closeSoon}
      onFocus={cancelClose}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setOpen(false);
        }
      }}
    >
      <Link
        ref={triggerRef}
        href={href}
        className={`${linkClass} inline-flex items-center gap-1.5`}
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="true"
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(false)}
      >
        {label}
        <svg
          viewBox="0 0 24 24"
          className={`size-3 shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </Link>

      {open && anchor
        ? createPortal(
            <div
              id={panelId}
              ref={panelRef}
              style={{ top: anchor.top, left: anchor.left }}
              /* The pointer handlers live here too: the panel is no longer a descendant of
                 the group in the DOM, so leaving the trigger for the panel would otherwise
                 read as leaving the menu entirely. */
              onPointerEnter={cancelClose}
              onPointerLeave={closeSoon}
              className="fixed z-[9990] w-max max-w-[min(46rem,90vw)] -translate-x-1/2 rounded-2xl border border-white/[0.12] bg-ownly-chrome/95 p-4 shadow-[0_28px_70px_-30px_rgba(0,0,0,0.85)] backdrop-blur-xl"
            >
        <div
          className={`grid gap-x-8 gap-y-5 ${
            columns === 3
              ? "grid-cols-3"
              : columns === 2
                ? "grid-cols-2"
                : "grid-cols-1"
          }`}
        >
          {sections.map((section, index) => (
            <div key={section.heading ?? index}>
              {section.heading ? (
                <p className="px-3 pb-1.5 text-copy font-semibold tracking-[0.01em] text-ownly-accent-text">
                  {section.heading}
                </p>
              ) : null}
              <ul>
                {section.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="block rounded-xl px-3 py-2 transition-colors duration-200 hover:bg-white/[0.07]"
                      onClick={() => setOpen(false)}
                    >
                      <span className="block text-copy font-semibold text-white/90">
                        {link.label}
                      </span>
                      {link.note ? (
                        <span className="mt-0.5 block text-copy text-white/60">
                          {link.note}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

              <div className="mt-3 border-t border-white/[0.09] px-3 pt-3">
                <Link
                  href={href}
                  className="text-copy font-semibold text-ownly-accent-text transition-colors hover:text-[#5ca3ff]"
                  onClick={() => setOpen(false)}
                >
                  {`All ${label.toLowerCase()}`}
                </Link>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
